import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

// Full-bleed banners. Each slide is a web (1920x620) and mobile (800x250) image in
// public/web_banners and public/mobile_banners, and the whole banner is the link.
const SLIDES = [
  {
    key: 'rf',
    alt: 'Magic: The Gathering – Reality Fracture',
    to: '/shop/tradingcardgames?category_id=PE4DDKCQJGSDNZO3WFPHIGZI',
    web: '/web_banners/web_rf.png',
    mobile: '/mobile_banners/mobile_rf.png',
  },
  {
    key: 'startrek',
    alt: 'Magic: The Gathering – Star Trek',
    to: '/shop/tradingcardgames?category_id=N6JGCEV665QLS56EJUYY2SCG',
    web: '/web_banners/web_startrek.png',
    mobile: '/mobile_banners/mobile_startrek.png',
  },
  {
    key: 'warhammer',
    alt: 'Warhammer',
    to: '/shop/tabletopgames?category_id=AENFPWJBS745UDMTDNHVWF7S',
    web: '/web_banners/web_warhammer.png',
    mobile: '/mobile_banners/mobile_warhammer.png',
  },
  {
    key: 'events',
    alt: 'Weekly events at Next Level Games',
    to: '/events',
    web: '/web_banners/web_events.png',
    mobile: '/mobile_banners/mobile_events.png',
  },
];

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 10000);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    startTimer();
  };
  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
    startTimer();
  };

  const slide = SLIDES[currentSlide];

  return (
    // pt-20 clears the fixed header; the banner fills the rest of the section edge to edge
    <section className="relative pt-20 bg-gray-900">
      <div className="relative w-full aspect-[800/250] md:aspect-[1920/620] overflow-hidden">
        <AnimatePresence initial={false}>
          <motion.div
            key={slide.key}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <Link to={slide.to} aria-label={slide.alt} className="block w-full h-full">
              <picture>
                <source media="(max-width: 767px)" srcSet={slide.mobile} />
                <img src={slide.web} alt={slide.alt} className="w-full h-full object-cover" />
              </picture>
            </Link>
          </motion.div>
        </AnimatePresence>

        {/* Prev / Next Arrows */}
        <button
          onClick={goToPrev}
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-3 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur transition-colors"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-4 h-4 sm:w-7 sm:h-7 text-white" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-3 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur transition-colors"
          aria-label="Next slide"
        >
          <ChevronRight className="w-4 h-4 sm:w-7 sm:h-7 text-white" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-2 sm:bottom-4 left-0 right-0 z-10 flex justify-center space-x-2">
          {SLIDES.map((s, index) => (
            <button
              key={s.key}
              onClick={() => { setCurrentSlide(index); startTimer(); }}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-8 bg-primary-500' : 'w-2 bg-white/60 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
